import dayjs from 'dayjs'
import SkTechApiClient from './base/SkTechApiClient'
import { ExtendedEvent, RecurrencePattern, RecurrenceRule } from '../pages/Extended Calendar/types'

const BASE = '/api/ProjectEventCalendar'

const ENUM_TO_LITERAL: Record<number, RecurrenceRule['pattern']> = { 
  0: 'none', 
  1: 'daily', 
  2: 'weekly', 
  3: 'monthly', 
  4: 'yearly' 
}

const LITERAL_TO_ENUM: Record<Exclude<RecurrencePattern, number>, number> = { 
  none: 0, 
  daily: 1, 
  weekly: 2, 
  monthly: 3, 
  yearly: 4 
}

function reviveMaster(raw: any): ExtendedEvent {
  // Check if title is a generic/default one
  const isGenericTitle = raw.title?.includes('Project event') || 
                        raw.title?.includes('Подія проєкту') ||
                        !raw.title ||
                        raw.title === '';
  
  return {
    id: `project_${raw.id}`,
    title: isGenericTitle ? (raw.projectName ?? 'Project event') : raw.title,
    start: new Date(raw.startDate),
    end: new Date(raw.endDate),
    type: 'project',
    userId: String(raw.authorId),
    participants: raw.participants,
    recurrence: raw.recurrence ? { 
      ...raw.recurrence, 
      pattern: ENUM_TO_LITERAL[raw.recurrence.pattern as number] ?? 'none' 
    } : { 
      pattern: 'none', 
      interval: 1, 
      end: { type: 'never' } 
    },
    projectId: raw.projectId,
    projectName: raw.projectName,
    ...raw,
  }
}

function reviveInstance(inst: any, master: any): ExtendedEvent {
  // Check if instance has its own title
  const isGenericTitle = inst.title?.includes('Project event') || 
                        inst.title?.includes('Подія проєкту') ||
                        !inst.title ||
                        inst.title === '';
  
  const masterIsGeneric = master.title?.includes('Project event') || 
                         master.title?.includes('Подія проєкту') ||
                         !master.title ||
                         master.title === '';
  
  return {
    id: `project_${inst.id}`,
    title: isGenericTitle 
      ? (masterIsGeneric ? (inst.projectName ?? master.projectName ?? 'Project event') : master.title)
      : inst.title,
    start: new Date(inst.startDate),
    end: new Date(inst.endDate),
    type: 'project',
    userId: String(master.authorId),
    participants: inst.participants,
    projectId: inst.projectId,
    projectName: inst.projectName,
    parentEventId: `project_${master.id}`,
    ...inst,
  }
}

const toDto = (e: any) => {
  const startDate = e.startDate ?? e.start
  const endDate = e.endDate ?? e.end
  const dto: any = {
    ...e,
    authorId: e.userId ?? e.authorId,
    startDate: startDate ? dayjs(startDate).format('YYYY-MM-DDTHH:mm:ss') : undefined,
    endDate: endDate ? dayjs(endDate).format('YYYY-MM-DDTHH:mm:ss') : undefined,
  }
  if (e.recurrence) {
    dto.recurrence = {
      ...e.recurrence,
      pattern: typeof e.recurrence.pattern === 'string' 
        ? (LITERAL_TO_ENUM as any)[e.recurrence.pattern] 
        : e.recurrence.pattern,
    }
  }
  Object.keys(dto).forEach(k => dto[k] === undefined && delete dto[k])
  return dto
}

const strip = (value: string | number) => String(value).replace('project_', '')

export default class ProjectEventsApiClient extends SkTechApiClient {
  async getAll(): Promise<ExtendedEvent[]> {
    try {
      const r = await this.Get(BASE)
      if (r.status !== 200) return []
      const flat: ExtendedEvent[] = []
      ;(r.data as any[]).forEach(raw => {
        const masterEv = reviveMaster(raw)
        flat.push(masterEv)
        if (Array.isArray(raw.instances)) {
          raw.instances.forEach((inst: any) => flat.push(reviveInstance(inst, raw)))
        }
      })
      return flat
    } catch {
      return []
    }
  }

  async addEvents(events: Omit<ExtendedEvent, 'id'>[]) {
    await Promise.all(events.map(e => this.addEvent(e)))
  }

  private async addEvent(evt: Omit<ExtendedEvent, 'id'>) {
    await this.Post(BASE, toDto(evt))
  }

  deleteEvent = async (
    id: string | number,
    isOccurrence = false,
    masterId?: string | number
  ) => {
    if (isOccurrence && masterId !== undefined) {
      await this.Delete(`${BASE}/${strip(masterId)}/occurrence/${strip(id)}`)
    } else {
      await this.Delete(`${BASE}/${strip(id)}`)
    }
  }

  updateSeries = async (id: string | number, data: any) =>
    this.Put(`${BASE}/${id}`, toDto(data))

  updateOccurrence = async (masterId: string | number, data: any) =>
    this.Put(`${BASE}/${masterId}/occurrence`, toDto(data))

  tombstoneOccurrence = async (
    masterId: string | number,
    data: { startDate: Date; endDate: Date; title?: string; isDeleted: boolean },
  ) =>
    this.Put(
      `${BASE}/${strip(masterId)}/occurrence`,
      toDto({ ...data, participants: [], needsApproval: false }),
    )

  approve = async (id: string | number) =>
    (await this.Post(`${BASE}/${id}/approve`, {})).status === 200
}